import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Car, Calendar, Clock, CreditCard, ChevronRight, ChevronLeft,
  CheckCircle2, ArrowLeft, Sparkles, Shield, AlertCircle, MessageSquare, Star, Phone,
  SunMedium, Droplets, Plus
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { API } from "../App";
import { SlotGrid } from "../components/SlotGrid";
import PromotionBanner from "../components/PromotionBanner";
import { useWashPerk, WashPerkBadge } from "../components/WashPerk";
import { resolveVehicleModel } from "../utils/vehicleDisplay";
import {
  trackBookingStarted,
  trackBookingFormSubmitted,
  trackPromoEntry,
} from "../utils/analytics";

// Import extracted components and constants
import { ColorSelectionModal, TeslaRearGlassModal, StepIndicator } from "../components/booking";
import { 
  PRICING_TIER_OVERRIDE, 
  TESLA_FULL_REAR_PREMIUM,
  FULL_CAR_TINT_IDS,
  PARTIAL_TINT_IDS,
  PERFORMANCE_TINT_IDS,
  CERAMIC_TINT_IDS
} from "../constants/pricingTiers";

// Quote Request Modal for services that require custom quotes
const QuoteRequestModal = ({ isOpen, onClose, service, vehicleData }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Bot traps (SEC-N1) — invisible to real customers.
  const [hpCompany, setHpCompany] = useState("");
  const openedAt = React.useRef(Date.now());

  React.useEffect(() => {
    if (isOpen) openedAt.current = Date.now();
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/services/quote-request`, {
        service_type: service?.service_id || 'custom',
        vehicle_make: vehicleData?.make || 'Not specified',
        vehicle_model: vehicleData?.model || 'Not specified',
        vehicle_model_other: vehicleData?.model === "Other" ? (vehicleData?.model_other || null) : null,
        vehicle_year: vehicleData?.year || 'Not specified',
        vehicle_size: vehicleData?.size || 'medium',
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        description: `Service: ${service?.name || 'Custom'}${form.message ? ` - ${form.message}` : ''}`,
        hp_company: hpCompany,
        form_elapsed_ms: Date.now() - openedAt.current
      });
      setSubmitted(true);
      toast.success('Quote request submitted! We\'ll contact you soon.');
    } catch (error) {
      // Even if API fails, show success (quote request is logged)
      setSubmitted(true);
      toast.success('Quote request received! We\'ll be in touch shortly.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setForm({ name: '', email: '', phone: '', message: '' });
    onClose();
  };

  if (!service) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-saturn-slate border-slate-700 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-orbitron text-xl text-white flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-saturn-orange" />
            Request Quote - {service.name}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {service.description}
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="text-center py-8">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="font-rajdhani font-bold text-xl text-white mb-2">Quote Request Sent!</h3>
            <p className="text-slate-400 mb-4">We'll review your request and contact you within 24 hours.</p>
            <p className="text-slate-500 text-sm mb-6">
              You can also reach us directly:<br/>
              <a href="tel:+353858237373" className="text-saturn-orange hover:underline">+353 85 823 7373</a>
              {" • "}
              <a href="https://wa.me/353858237373" target="_blank" rel="noopener noreferrer" className="text-green-500 hover:underline">WhatsApp</a>
            </p>
            <Button onClick={handleClose} className="bg-saturn-orange hover:bg-saturn-orange-hover">
              Continue Browsing
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Honeypot — visually hidden, not focusable. Only bots fill it. */}
            <div aria-hidden="true" className="absolute left-[-9999px] top-auto w-px h-px overflow-hidden">
              <label htmlFor="booking-quote-company-website">Company website</label>
              <input
                id="booking-quote-company-website"
                name="company_website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={hpCompany}
                onChange={(e) => setHpCompany(e.target.value)}
              />
            </div>
            {vehicleData?.make && (
              <div className="bg-slate-800/50 rounded-lg p-3 text-sm">
                <span className="text-slate-400">Vehicle: </span>
                <span className="text-white font-medium">
                  {vehicleData.year} {vehicleData.make} {resolveVehicleModel({ model: vehicleData.model, model_other: vehicleData.model_other })}
                </span>
              </div>
            )}
            
            <div>
              <Label className="text-slate-400">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your full name"
                className="bg-slate-800 border-slate-700 mt-1"
                required
              />
            </div>
            
            <div>
              <Label className="text-slate-400">Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="your@email.com"
                className="bg-slate-800 border-slate-700 mt-1"
                required
              />
            </div>
            
            <div>
              <Label className="text-slate-400">Phone *</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+353 85 123 4567"
                className="bg-slate-800 border-slate-700 mt-1"
                required
              />
            </div>
            
            <div>
              <Label className="text-slate-400">Additional Details (Optional)</Label>
              <Input
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Color preference, special requests, etc."
                className="bg-slate-800 border-slate-700 mt-1"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={handleClose} className="flex-1 border-slate-700">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1 bg-saturn-orange hover:bg-saturn-orange-hover">
                {submitting ? 'Sending...' : 'Submit Quote Request'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Step 1: Vehicle Selection
const VehicleStep = ({ data, setData, onNext }) => {
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showManualSize, setShowManualSize] = useState(false);

  useEffect(() => {
    const fetchMakes = async () => {
      try {
        const response = await axios.get(`${API}/vehicles/makes`);
        setMakes(response.data);
      } catch (error) {
        toast.error("Failed to load vehicle makes");
      } finally {
        setLoading(false);
      }
    };
    fetchMakes();
  }, []);

  useEffect(() => {
    const fetchModels = async () => {
      if (!data.make) return;
      try {
        const response = await axios.get(`${API}/vehicles/models/${data.make}`);
        setModels(response.data);
      } catch (error) {
        toast.error("Failed to load models");
      }
    };
    fetchModels();
  }, [data.make]);

  const handleModelChange = (modelName) => {
    const model = models.find((m) => m.name === modelName);
    // If "Other" is selected, require manual size selection AND free-text model name
    if (modelName === "Other" || !model?.size_category) {
      setShowManualSize(true);
      setData({
        ...data,
        model: modelName,
        model_other: "",  // Reset custom model on new "Other" selection
        size: "",  // User must select manually
      });
    } else {
      setShowManualSize(false);
      setData({
        ...data,
        model: modelName,
        model_other: "",  // Clear custom text when a real model is picked
        size: model?.size_category || "",
      });
    }
  };

  // Years: 2026 down to 2001, then <2000
  const years = [...Array.from({ length: 26 }, (_, i) => (2026 - i).toString()), "<2000"];

  // Size options for manual selection
  const sizeOptions = [
    { value: "hatch", label: "Hatch (Small)" },
    { value: "saloon", label: "Saloon (Medium)" },
    { value: "estate", label: "Estate / Small Jeep (Large)" },
    { value: "suv", label: "Jeep / MPV (Large SUV)" },
  ];

  // Map backend size to display label
  const getSizeLabel = (size) => {
    const labels = { hatch: "Hatch", saloon: "Saloon", estate: "Estate", suv: "Jeep/MPV" };
    return labels[size] || size;
  };

  const isOtherModel = data.model === "Other";
  const canProceed = data.make && data.model && data.year && data.size && (!isOtherModel || (data.model_other && data.model_other.trim().length > 0));

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white">
          Select Your Vehicle
        </h2>
        <p className="text-slate-400 mt-2 font-inter">
          Tell us about your vehicle so we can provide accurate pricing
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label className="text-slate-300 font-rajdhani">Make</Label>
          <Select value={data.make || ""} onValueChange={(v) => { setData({ ...data, make: v, model: "", model_other: "", size: "" }); setShowManualSize(false); }}>
            <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white" data-testid="vehicle-make-select">
              <SelectValue placeholder="Select make" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 max-h-64 overflow-y-auto">
              {makes.map((make) => (
                <SelectItem key={make.name} value={make.name} className="text-white hover:bg-saturn-orange/20">
                  {make.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-slate-300 font-rajdhani">Model</Label>
          <Select value={data.model || ""} onValueChange={handleModelChange} disabled={!data.make}>
            <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white" data-testid="vehicle-model-select">
              <SelectValue placeholder="Select model" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 max-h-64 overflow-y-auto">
              {models.map((model) => (
                <SelectItem key={model.name} value={model.name} className="text-white hover:bg-saturn-orange/20">
                  {model.name} {model.size_category && `(${getSizeLabel(model.size_category)})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isOtherModel && (
            <div className="pt-2">
              <Label className="text-slate-300 font-rajdhani text-sm">
                What's the model called? <span className="text-saturn-orange">*</span>
              </Label>
              <Input
                value={data.model_other || ""}
                onChange={(e) => setData({ ...data, model_other: e.target.value })}
                placeholder="e.g. Yeti, Citigo, Cybertruck"
                className="bg-slate-900/50 border-slate-700 text-white mt-1"
                data-testid="vehicle-model-other-input"
                maxLength={60}
              />
              <p className="text-slate-500 text-xs mt-1">
                We keep adding models — telling us the exact name helps us list it next time.
              </p>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-slate-300 font-rajdhani">Year</Label>
          <Select value={data.year || ""} onValueChange={(v) => setData({ ...data, year: v })}>
            <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white" data-testid="vehicle-year-select">
              <SelectValue placeholder="Select year" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 max-h-64 overflow-y-auto">
              {years.map((year) => (
                <SelectItem key={year} value={year} className="text-white hover:bg-saturn-orange/20">
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Manual size selection if "Other" or no size detected */}
        {(showManualSize || !data.size) && data.model && (
          <div className="space-y-2">
            <Label className="text-slate-300 font-rajdhani">Vehicle Size *</Label>
            <Select value={data.size || ""} onValueChange={(v) => setData({ ...data, size: v })}>
              <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white" data-testid="vehicle-size-select">
                <SelectValue placeholder="Select vehicle size" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {sizeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="text-white hover:bg-saturn-orange/20">
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Show auto-detected size */}
        {data.size && !showManualSize && (
          <div className="space-y-2">
            <Label className="text-slate-300 font-rajdhani">Vehicle Size</Label>
            <div className="glass rounded-lg px-4 py-3 flex items-center gap-2">
              <Car className="w-5 h-5 text-saturn-orange" />
              <span className="text-white font-rajdhani">{getSizeLabel(data.size)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end pt-6">
        <Button
          onClick={onNext}
          disabled={!canProceed}
          className="bg-saturn-orange hover:bg-saturn-orange-hover text-white font-rajdhani font-bold px-8 py-3"
          data-testid="vehicle-next-btn"
        >
          Continue <ChevronRight className="ml-2 w-5 h-5" />
        </Button>
      </div>
    </motion.div>
  );
};

// Step 2: Service Selection with Package Tiers
/**
 * Remove a hardcoded free-wash promise from catalog copy.
 *
 * Some service descriptions and perk chips were written when the wash was
 * a permanent feature (e.g. "All windows except windshield (includes FREE
 * car wash)"). The wash is now rule-driven, so that copy must disappear
 * the moment the rule is paused — otherwise the card promises a wash the
 * booking will not grant.
 */
const stripWashPhrases = (text) =>
  (text || "")
    .replace(/\s*\((?:[^()]*\b(?:free\s*)?car\s*wash[^()]*)\)/gi, "")
    .replace(/\s*[-–·,]\s*(?:includes\s+)?free\s*car\s*wash\b\.?/gi, "")
    .trim();

const ServiceStep = ({ data, setData, onNext, onBack }) => {
  const [services, setServices] = useState([]);
  const wash = useWashPerk();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upsell, setUpsell] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [colorModalService, setColorModalService] = useState(null);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [quoteModalService, setQuoteModalService] = useState(null);
  // Tesla Model 3 rear glass modal state
  const [teslaRearGlassModalOpen, setTeslaRearGlassModalOpen] = useState(false);
  const [teslaRearGlassService, setTeslaRearGlassService] = useState(null);

  // Tesla Model 3 rear glass premium constant
  const TESLA_FULL_REAR_PREMIUM = 40;
  
  // Check if vehicle is Tesla Model 3
  const isTeslaModel3 = data.make === "Tesla" && data.model === "Model 3";
  
  // Services that include rear glass tinting (need Tesla option)
  const rearGlassServices = [
    'svc_perf_rear', 'svc_perf_full',
    'svc_ceramic_rear', 'svc_ceramic_full'
  ];

  // Performance to Ceramic upsell mapping
  const ceramicUpsellMap = {
    'svc_perf_rear': { ceramicId: 'svc_ceramic_rear', name: 'Savvy Ceramic Rear Tint', price: 'saloon' },
    'svc_perf_full': { ceramicId: 'svc_ceramic_full', name: 'Savvy Ceramic Full Car', price: 'saloon' },
    'svc_perf_windshield': { ceramicId: 'svc_ceramic_windshield', name: 'Savvy Ceramic Windshield', price: 'flat' },
    'svc_perf_front_doors': { ceramicId: 'svc_ceramic_front_doors', name: 'Savvy Ceramic Front Doors', price: 'flat' },
  };
  
  // Reverse mapping: Ceramic to Performance (for exclusivity)
  const performanceFromCeramicMap = {
    'svc_ceramic_rear': 'svc_perf_rear',
    'svc_ceramic_full': 'svc_perf_full',
    'svc_ceramic_windshield': 'svc_perf_windshield',
    'svc_ceramic_front_doors': 'svc_perf_front_doors',
  };
  
  // Check if any Performance tint is selected WITHOUT its Ceramic equivalent already selected
  const selectedPerformanceTints = data.services.filter(id => {
    if (!Object.keys(ceramicUpsellMap).includes(id)) return false;
    // Only include if the Ceramic version is NOT already selected
    const ceramicId = ceramicUpsellMap[id].ceramicId;
    return !data.services.includes(ceramicId);
  });
  const showCeramicUpsell = selectedPerformanceTints.length > 0;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, packagesRes] = await Promise.all([
          axios.get(`${API}/services/`),
          axios.get(`${API}/services/packages`)
        ]);
        setServices(servicesRes.data);
        setPackages(packagesRes.data.packages || []);
      } catch (error) {
        toast.error("Failed to load services");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const checkUpsell = async () => {
      // Reset stale upsell state whenever the selection is empty, so that
      // a cascade-removal (e.g. deselecting the last buffing stage which
      // also strips Ceramic Coating) does not leave the "Add Ceramic
      // Coating…" banner stuck on-screen.
      if (data.services.length === 0) {
        setUpsell(null);
        return;
      }
      try {
        const mainService = data.services[0];
        const response = await axios.get(`${API}/services/upsell/${mainService}`);
        if (response.data.has_upsell) {
          setUpsell(response.data);
        } else {
          // No upsell for the current main service — clear any previously
          // set recommendation instead of letting a stale one hang around.
          setUpsell(null);
        }
      } catch (error) {
        // Upsell not critical - log for debugging but don't disrupt user experience
        console.debug("Upsell check failed:", error.message);
      }
    };
    checkUpsell();
  }, [data.services]);

  // Constants for ceramic coating dependency
  const buffingServices = ['svc_buff_stage1', 'svc_buff_stage2', 'svc_buff_stage3'];
  const ceramicCoatingId = 'svc_paint_ceramic_coating';
  
  // Full car tint exclusivity - full car includes rear + front doors, so they shouldn't be selectable together
  const fullCarTintServices = ['svc_perf_full', 'svc_ceramic_full'];
  const rearTintServices = ['svc_perf_rear', 'svc_ceramic_rear'];
  const frontDoorServices = ['svc_perf_front_doors', 'svc_ceramic_front_doors'];
  const partialTintServices = [...rearTintServices, ...frontDoorServices];
  
  // Check if any full car tint is selected
  const hasFullCarTint = fullCarTintServices.some(id => data.services.includes(id));
  // Check if any partial tint (rear or front doors) is selected
  const hasPartialTint = partialTintServices.some(id => data.services.includes(id));

  const handleServiceClick = (serviceId, service) => {
    // Check if out of stock
    if (service?.out_of_stock) {
      toast.error(`${service.name} is OUT OF STOCK. Please upgrade to Performance or Savvy Ceramic.`);
      return;
    }
    
    // Check if requires quote
    if (service?.requires_quote) {
      setQuoteModalService(service);
      setQuoteModalOpen(true);
      return;
    }
    
    // Check if has color option (Roof Wrap / Chrome Delete)
    if (service?.has_color_option) {
      setColorModalService(service);
      setColorModalOpen(true);
      return;
    }
    
    // Check if Tesla Model 3 and selecting a rear glass service
    if (isTeslaModel3 && rearGlassServices.includes(serviceId) && !data.services.includes(serviceId)) {
      // Show Tesla rear glass selection modal
      setTeslaRearGlassService(service);
      setTeslaRearGlassModalOpen(true);
      return;
    }
    
    // Check if trying to add Ceramic Coating without a buffing service
    if (serviceId === ceramicCoatingId && !data.services.includes(serviceId)) {
      // Adding ceramic coating - check if any buffing service is selected
      const hasBuffing = buffingServices.some(id => data.services.includes(id));
      if (!hasBuffing) {
        toast.error(
          'Ceramic Paint Coating requires paint correction first. Please select Stage 1, 2, or 3 Buffing before adding Ceramic Coating.',
          { duration: 5000 }
        );
        // Scroll to polishing section
        const polishingSection = document.getElementById('category-polishing');
        if (polishingSection) {
          polishingSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
    }
    
    // Full car tint exclusivity - can't select full car if rear or front doors already selected
    if (fullCarTintServices.includes(serviceId) && !data.services.includes(serviceId)) {
      if (hasPartialTint) {
        toast.error(
          'Full Car Tint already includes rear and front door windows. Please deselect Rear Tint or Front Doors first.',
          { duration: 5000 }
        );
        return;
      }
    }
    
    // Can't select rear or front doors if full car tint is already selected
    if (partialTintServices.includes(serviceId) && !data.services.includes(serviceId)) {
      if (hasFullCarTint) {
        toast.error(
          'Full Car Tint is already selected which includes all windows. No need to add Rear Tint or Front Doors separately.',
          { duration: 5000 }
        );
        return;
      }
    }
    
    // Normal toggle
    toggleService(serviceId, service);
  };
  
  // Helper function to apply all exclusivity logic when adding a service
  const applyExclusivityLogic = (serviceId, currentServices) => {
    let newServices = [...currentServices];
    
    // Full car tint exclusivity - remove partial tints if selecting full car
    if (fullCarTintServices.includes(serviceId)) {
      const removedPartials = partialTintServices.filter(id => newServices.includes(id));
      if (removedPartials.length > 0) {
        newServices = newServices.filter(id => !partialTintServices.includes(id));
        toast.info('Partial tints removed - Full Car Tint covers all windows.', { duration: 3000 });
      }
    }
    
    // Can't add partial tint if full car is selected
    if (partialTintServices.includes(serviceId)) {
      const hasFullCar = fullCarTintServices.some(id => newServices.includes(id));
      if (hasFullCar) {
        toast.error('Full Car Tint already includes all windows.', { duration: 3000 });
        return null; // Don't add this service
      }
    }
    
    // Performance/Ceramic exclusivity
    if (ceramicUpsellMap[serviceId]) {
      // Adding Performance - remove Ceramic equivalent
      const ceramicEquivalent = ceramicUpsellMap[serviceId].ceramicId;
      if (newServices.includes(ceramicEquivalent)) {
        newServices = newServices.filter(s => s !== ceramicEquivalent);
        toast.info('Ceramic version replaced with Performance version.', { duration: 3000 });
      }
    }
    if (performanceFromCeramicMap[serviceId]) {
      // Adding Ceramic - remove Performance equivalent
      const perfEquivalent = performanceFromCeramicMap[serviceId];
      if (newServices.includes(perfEquivalent)) {
        newServices = newServices.filter(s => s !== perfEquivalent);
        toast.info('Upgraded to Savvy Ceramic version!', { duration: 3000 });
      }
    }
    
    // Add the new service
    if (!newServices.includes(serviceId)) {
      newServices.push(serviceId);
    }
    
    return newServices;
  };
  
  // Handle Tesla rear glass selection - Half (standard)
  const handleTeslaHalfRear = () => {
    if (teslaRearGlassService) {
      const serviceId = teslaRearGlassService.service_id;
      
      // Apply exclusivity logic
      const newServices = applyExclusivityLogic(serviceId, data.services);
      if (newServices === null) {
        // Service couldn't be added due to exclusivity
        setTeslaRearGlassModalOpen(false);
        setTeslaRearGlassService(null);
        return;
      }
      
      // Handle Tesla rear glass option transfer if replacing ceramic with performance
      let newTeslaOptions = { ...data.teslaRearGlassOption };
      const perfEquivalent = performanceFromCeramicMap[serviceId];
      if (perfEquivalent && newTeslaOptions[perfEquivalent]) {
        delete newTeslaOptions[perfEquivalent];
      }
      newTeslaOptions[serviceId] = 'half';
      
      setData({
        ...data,
        services: newServices,
        teslaRearGlassOption: newTeslaOptions
      });
      toast.success(`${teslaRearGlassService.name} added with Half Rear Glass`);
    }
    setTeslaRearGlassModalOpen(false);
    setTeslaRearGlassService(null);
  };
  
  // Handle Tesla rear glass selection - Full (+€40 premium)
  const handleTeslaFullRear = () => {
    if (teslaRearGlassService) {
      const serviceId = teslaRearGlassService.service_id;
      
      // Apply exclusivity logic
      const newServices = applyExclusivityLogic(serviceId, data.services);
      if (newServices === null) {
        // Service couldn't be added due to exclusivity
        setTeslaRearGlassModalOpen(false);
        setTeslaRearGlassService(null);
        return;
      }
      
      // Handle Tesla rear glass option transfer if replacing ceramic with performance
      let newTeslaOptions = { ...data.teslaRearGlassOption };
      const perfEquivalent = performanceFromCeramicMap[serviceId];
      if (perfEquivalent && newTeslaOptions[perfEquivalent]) {
        delete newTeslaOptions[perfEquivalent];
      }
      newTeslaOptions[serviceId] = 'full';
      
      setData({
        ...data,
        services: newServices,
        teslaRearGlassOption: newTeslaOptions
      });
      toast.success(`${teslaRearGlassService.name} added with Full Rear Glass (+€40)`);
    }
    setTeslaRearGlassModalOpen(false);
    setTeslaRearGlassService(null);
  };

  const toggleService = (serviceId, service) => {
    // Check if out of stock
    if (service?.out_of_stock) {
      toast.error(`${service.name} is OUT OF STOCK. Please upgrade to Performance or Savvy Ceramic.`);
      return;
    }
    
    const isRemoving = data.services.includes(serviceId);
    let newServices = isRemoving
      ? data.services.filter((s) => s !== serviceId)
      : [...data.services, serviceId];
    
    // Initialize Tesla rear glass options for later modification
    let newTeslaRearGlassOption = { ...data.teslaRearGlassOption };
    
    // BUFFING STAGE EXCLUSION LOGIC
    // Only one buffing stage can be selected at a time
    // If selecting a buffing stage, remove any other buffing stages
    if (!isRemoving && buffingServices.includes(serviceId)) {
      // Adding a buffing service - remove any other buffing services
      const otherBuffingServices = buffingServices.filter(id => id !== serviceId);
      const hadOtherBuffing = otherBuffingServices.some(id => data.services.includes(id));
      newServices = newServices.filter(s => !otherBuffingServices.includes(s));
      
      if (hadOtherBuffing) {
        toast.info('Previous buffing stage replaced. Only one buffing stage can be selected.', { duration: 3000 });
      }
    }
    
    // PERFORMANCE / CERAMIC EXCLUSIVITY LOGIC
    // Can't have both Performance and Ceramic versions of the same service
    if (!isRemoving) {
      // If selecting a Performance service, remove its Ceramic equivalent
      if (ceramicUpsellMap[serviceId]) {
        const ceramicEquivalent = ceramicUpsellMap[serviceId].ceramicId;
        if (newServices.includes(ceramicEquivalent)) {
          newServices = newServices.filter(s => s !== ceramicEquivalent);
          toast.info('Ceramic version replaced with Performance version.', { duration: 3000 });
        }
      }
      // If selecting a Ceramic service, remove its Performance equivalent
      if (performanceFromCeramicMap[serviceId]) {
        const perfEquivalent = performanceFromCeramicMap[serviceId];
        if (newServices.includes(perfEquivalent)) {
          newServices = newServices.filter(s => s !== perfEquivalent);
          // Transfer Tesla rear glass option if applicable
          if (newTeslaRearGlassOption[perfEquivalent]) {
            newTeslaRearGlassOption[serviceId] = newTeslaRearGlassOption[perfEquivalent];
            delete newTeslaRearGlassOption[perfEquivalent];
          }
          toast.info('Upgraded to Savvy Ceramic version!', { duration: 3000 });
        }
      }
    }
    
    // If removing a buffing service and ceramic coating is selected, also remove ceramic coating
    if (isRemoving && buffingServices.includes(serviceId)) {
      const remainingBuffing = buffingServices.filter(id => id !== serviceId && newServices.includes(id));
      if (remainingBuffing.length === 0 && newServices.includes(ceramicCoatingId)) {
        newServices = newServices.filter(s => s !== ceramicCoatingId);
        toast.warning('Ceramic Paint Coating removed - it requires a buffing service.', { duration: 4000 });
      }
    }
    
    // If removing a Tesla rear glass service, also remove the option
    if (isRemoving && rearGlassServices.includes(serviceId)) {
      delete newTeslaRearGlassOption[serviceId];
    }
    
    setData({
      ...data,
      services: newServices,
      teslaRearGlassOption: newTeslaRearGlassOption,
    });
  };

  const handleGlossBlackSelect = () => {
    if (colorModalService) {
      toggleService(colorModalService.service_id, colorModalService);
      setColorModalOpen(false);
      setColorModalService(null);
    }
  };

  const handleCustomColorSelect = () => {
    // Open quote modal for custom color
    setQuoteModalService(colorModalService);
    setColorModalOpen(false);
    setColorModalService(null);
    setQuoteModalOpen(true);
  };

  // Map vehicle size to pricing size (backend uses hatch/saloon/estate/suv)
  // Uses pricing override for luxury saloons
  const getPricingSize = () => {
    // First check if there's a pricing override for this model
    if (data.model && PRICING_TIER_OVERRIDE[data.model]) {
      return PRICING_TIER_OVERRIDE[data.model];
    }
    
    const sizeMap = {
      'small': 'hatch',
      'medium': 'saloon',
      'large': 'estate',
      'suv': 'suv',
      'hatch': 'hatch',
      'saloon': 'saloon',
      'estate': 'estate'
    };
    return sizeMap[data.size] || 'saloon';
  };

  const getServicePrice = (service) => {
    const pricingSize = getPricingSize();
    return service.price_by_size[pricingSize] || service.price_by_size[data.size] || service.base_price;
  };

  const totalPrice = services
    .filter((s) => data.services.includes(s.service_id))
    .reduce((sum, s) => {
      const price = getServicePrice(s);
      const quantity = s.allows_quantity ? (data.serviceQuantities[s.service_id] || 1) : 1;
      return sum + (price * quantity);
    }, 0);

  const totalDuration = services
    .filter((s) => data.services.includes(s.service_id))
    .reduce((sum, s) => {
      const quantity = s.allows_quantity ? (data.serviceQuantities[s.service_id] || 1) : 1;
      return sum + (s.duration_minutes * quantity);
    }, 0);

  const totalDays = services
    .filter((s) => data.services.includes(s.service_id))
    .reduce((sum, s) => sum + (s.days_required || 0), 0);

  const canProceed = data.services.length > 0;

  // Calculate total price for selected services
  const calculateTotal = () => {
    let total = 0;
    const pricingSize = getPricingSize();
    
    data.services.forEach(serviceId => {
      const service = services.find(s => s.service_id === serviceId);
      if (service) {
        const quantity = data.serviceQuantities?.[serviceId] || 1;
        const price = service.price_by_size?.[pricingSize] || service.base_price || 0;
        total += price * quantity;
        
        // Add Tesla Model 3 full rear glass premium if applicable
        if (data.teslaRearGlassOption?.[serviceId] === 'full') {
          total += TESLA_FULL_REAR_PREMIUM;
        }
      }
    });
    return total;
  };

  // Calculate total duration
  const calculateDuration = () => {
    let duration = 0;
    data.services.forEach(serviceId => {
      const service = services.find(s => s.service_id === serviceId);
      if (service) {
        const quantity = data.serviceQuantities?.[serviceId] || 1;
        duration += (service.duration_minutes || 0) * quantity;
      }
    });
    return duration;
  };

  // Handle upgrade to Ceramic
  const handleUpgradeToCeramic = () => {
    const pricingSize = getPricingSize();
    let newServices = [...data.services];
    let newTeslaOptions = { ...data.teslaRearGlassOption };
    let includesCarwash = false;
    
    // Replace each Performance service with its Ceramic equivalent
    selectedPerformanceTints.forEach(perfId => {
      const upgrade = ceramicUpsellMap[perfId];
      if (upgrade) {
        // Remove Performance service
        newServices = newServices.filter(id => id !== perfId);
        // Add Ceramic service
        if (!newServices.includes(upgrade.ceramicId)) {
          newServices.push(upgrade.ceramicId);
        }
        // Transfer Tesla rear glass option if applicable
        if (newTeslaOptions[perfId]) {
          newTeslaOptions[upgrade.ceramicId] = newTeslaOptions[perfId];
          delete newTeslaOptions[perfId];
        }
        // Does the wash perk actually cover what we are swapping INTO?
        // This used to be a hardcoded "rear or full", which promised a
        // free wash even when the rule was paused or didn't cover it.
        if (wash.isCovered(upgrade.ceramicId)) {
          includesCarwash = true;
        }
      }
    });
    
    setData({
      ...data,
      services: newServices,
      teslaRearGlassOption: newTeslaOptions
    });
    
    // Show appropriate message based on whether carwash is included
    if (includesCarwash) {
      toast.success('Upgraded to Savvy Ceramic! 5-year warranty + FREE carwash included.', { duration: 4000 });
    } else {
      toast.success('Upgraded to Savvy Ceramic! 5-year warranty + superior heat rejection.', { duration: 4000 });
    }
  };

  // Calculate ceramic upgrade price difference
  const calculateCeramicUpgradePrice = () => {
    const pricingSize = getPricingSize();
    let perfTotal = 0;
    let ceramicTotal = 0;
    
    selectedPerformanceTints.forEach(perfId => {
      const perfService = services.find(s => s.service_id === perfId);
      const upgrade = ceramicUpsellMap[perfId];
      if (perfService && upgrade) {
        const ceramicService = services.find(s => s.service_id === upgrade.ceramicId);
        if (ceramicService) {
          perfTotal += perfService.price_by_size?.[pricingSize] || perfService.base_price || 0;
          ceramicTotal += ceramicService.price_by_size?.[pricingSize] || ceramicService.base_price || 0;
        }
      }
    });
    
    return ceramicTotal - perfTotal;
  };

  // Group services by category
  const categoryLabels = {
    'tinting-performance': 'Performance Tinting (1yr Warranty)',
    'tinting-ceramic': 'Savvy Ceramic (5yr Warranty)',
    'tinting-chameleon': 'Chameleon Film',
    'wrapping': 'Vehicle Wrapping',
    'polishing': 'Paint Correction',
    'coating': 'Ceramic Coating',
    'tint-removal': 'Tint Removal',
    'misc': 'Other Services'
  };

  const groupedServices = services.reduce((acc, service) => {
    const cat = service.category || 'misc';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(service);
    return acc;
  }, {});

  // Order categories
  const categoryOrder = ['tinting-performance', 'tinting-ceramic', 'tinting-chameleon', 'wrapping', 'polishing', 'coating', 'tint-removal', 'misc'];

  // Super-tabs — group the 8 fine-grained categories into 4 calm buckets.
  // Order inside each bucket preserves the pre-existing categoryOrder,
  // so the display sequence and any Ceramic-upsell adjacency logic is
  // unchanged from the stacked layout.
  const superTabs = [
    {
      id: 'tint',
      label: 'Window Tint',
      icon: SunMedium,
      categories: ['tinting-performance', 'tinting-ceramic', 'tinting-chameleon', 'tint-removal'],
    },
    {
      id: 'protection',
      label: 'Wrapping',
      icon: Shield,
      categories: ['wrapping'],
    },
    {
      id: 'detailing',
      label: 'Detailing / Ceramic',
      icon: Droplets,
      categories: ['polishing', 'coating'],
    },
    {
      id: 'addons',
      label: 'Add-ons',
      icon: Plus,
      categories: ['misc'],
    },
  ];

  const superTabsWithContent = superTabs
    .map((t) => ({
      ...t,
      categories: t.categories.filter((c) => (groupedServices[c] || []).length > 0),
    }))
    .filter((t) => t.categories.length > 0);

  // Count how many currently-selected services fall inside each super-tab —
  // used to surface a subtle "N" badge on the tab trigger so users can see
  // their selections without leaving the current tab.
  const selectedCountByTab = superTabsWithContent.reduce((acc, t) => {
    const idsInTab = new Set(
      t.categories.flatMap((c) => (groupedServices[c] || []).map((s) => s.service_id))
    );
    acc[t.id] = data.services.filter((sid) => idsInTab.has(sid)).length;
    return acc;
  }, {});

  const [activeServiceTab, setActiveServiceTab] = React.useState(
    superTabsWithContent[0]?.id || 'tint'
  );

  // Keep the active tab valid if services load after mount.
  React.useEffect(() => {
    if (superTabsWithContent.length > 0 &&
        !superTabsWithContent.find((t) => t.id === activeServiceTab)) {
      setActiveServiceTab(superTabsWithContent[0].id);
    }
  }, [superTabsWithContent.length]);

  // ---- The ONE upsell slot's priority ladder -------------------------
  // Order matters: the ceramic upgrade is worth the most and carries the
  // wash message itself, so it outranks the wash lines. Exactly one of
  // these modes can be truthy at a time.
  const ceramicTargetIds = selectedPerformanceTints
    .map((id) => ceramicUpsellMap[id]?.ceramicId)
    .filter(Boolean);
  // The wash wording on the upgrade bar is driven by the LIVE rules for
  // the service we would swap INTO — not by a hardcoded "rear or full",
  // which used to promise a wash the rules might not cover.
  const upgradeHasWash = ceramicTargetIds.some((id) => wash.isCovered(id));
  const ceramicUpgradeLabel = ceramicTargetIds.length === 1
    ? (services.find((s) => s.service_id === ceramicTargetIds[0])?.name
       || "Savvy Ceramic")
    : "Savvy Ceramic";

  // Pick the most complete covered service, so we never nudge someone
  // towards Ceramic REAR when they already have a FULL car tint. Prefer
  // one in the tab they are actually looking at: with the standing
  // "all Ceramic" rule live, raw highest-price picks Ceramic Paint
  // Coating (€300) and offers a tint customer a paint job.
  const washCandidates = wash.coveredIds.length
    ? services.filter((s) => wash.isCovered(s.service_id) && !s.out_of_stock
                             && !s.requires_quote
                             && !data.services.includes(s.service_id))
    : [];
  const activeTabCategories = new Set(
    (superTabsWithContent.find((t) => t.id === activeServiceTab)?.categories) || []
  );
  const washNudgeTarget = (() => {
    if (!washCandidates.length) return null;
    const byValue = (a, b) => getServicePrice(b) - getServicePrice(a);
    const inTab = washCandidates
      .filter((s) => activeTabCategories.has(s.category))
      .sort(byValue);
    return inTab[0] || [...washCandidates].sort(byValue)[0] || null;
  })();

  const addOnUpsell = upsell
    && !data.services.includes(upsell.upsell_service.service_id)
    // Never re-offer the ceramic equivalent — that is the upgrade bar's
    // job, and offering both is what made the two bars duplicates.
    && !ceramicTargetIds.includes(upsell.upsell_service.service_id)
    ? upsell : null;

  const bannerMode = showCeramicUpsell ? "upgrade"
    : wash.anyCovered(data.services) ? "wash-included"
    : washNudgeTarget ? "wash-nudge"
    : addOnUpsell ? "addon"
    : null;


  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      {/* Sticky Header with Continue Button - Always visible.
          The upsell slot lives INSIDE this sticky block as a second row:
          it used to be its own `sticky top-32` element, which collided
          with this bar (and with the service tabs, also `top-32`) and
          clipped its own first line on a phone. */}
      <div className="sticky top-16 z-40 -mx-4 px-4 py-3 bg-saturn-black/95 backdrop-blur-lg border-b border-slate-800/50">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Summary info */}
          <div className="flex-1 min-w-0">
            {data.services.length > 0 ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-saturn-orange font-rajdhani font-bold text-lg sm:text-xl">
                    €{calculateTotal()}
                  </span>
                  <span className="text-slate-400 text-sm hidden sm:inline">total</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span>{data.services.length} service{data.services.length !== 1 ? 's' : ''}</span>
                  <span className="hidden sm:inline">•</span>
                  <Clock className="w-4 h-4 hidden sm:inline" />
                  <span className="hidden sm:inline">{calculateDuration()} mins</span>
                </div>
              </div>
            ) : (
              <p className="text-slate-400 text-sm">Select services to continue</p>
            )}
          </div>
          
          {/* Right: Continue Button */}
          <Button
            onClick={onNext}
            disabled={!canProceed}
            className={`font-rajdhani font-bold px-4 sm:px-6 py-2 text-sm sm:text-base whitespace-nowrap ${
              canProceed 
                ? 'bg-saturn-orange hover:bg-saturn-orange-hover text-white' 
                : 'bg-slate-700 text-slate-400 cursor-not-allowed'
            }`}
            data-testid="service-next-btn-sticky"
          >
            Continue <ChevronRight className="ml-1 w-4 h-4" />
          </Button>
        </div>

      {/* ========================= ONE UPSELL SLOT =========================
          The page used to stack three promo bars (sticky ceramic bar +
          inline orange upsell card + teal wash card), which pushed the
          services below the fold on a phone and contradicted itself:
          "+€40" (difference) next to "for €270" (total), and a wash nudge
          that offered Ceramic REAR to someone who had already selected a
          FULL car tint.

          Now there is exactly ONE slot with a strict priority ladder, so
          two promo bars can never be on screen together:

            1. ceramic upgrade  — an upgradeable Performance tint is
               selected. SWAPS it for the like-for-like Ceramic service
               and quotes the DIFFERENCE.
            2. wash included    — a covered service is already selected:
               a confirmation line, not an upsell.
            3. wash nudge       — a live rule exists but nothing covered
               is selected and there is nothing to upgrade.
            4. add-on upsell    — the backend recommendation (e.g.
               Ceramic Coating after paint correction), but never the
               ceramic equivalent of what is already selected, which is
               what made bars 1 and 2 duplicates.
      */}
      {bannerMode && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className={`-mx-4 mt-2 px-4 py-2 border-t ${
            bannerMode === "upgrade"
              ? "bg-gradient-to-r from-emerald-900/90 to-teal-900/90 border-emerald-500/30"
              : bannerMode === "addon"
                ? "bg-saturn-orange/10 border-saturn-orange/30"
                : "bg-cyan-500/10 border-cyan-500/30"
          }`}
          data-testid="service-upsell-banner"
          data-mode={bannerMode}
        >
          <div className="flex items-center justify-between gap-2">
            {bannerMode === "upgrade" && (
              <>
                <div className="flex items-center gap-2 min-w-0">
                  <Sparkles className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <p className="text-emerald-300 font-rajdhani font-bold text-xs sm:text-sm leading-tight min-w-0">
                    Upgrade to {ceramicUpgradeLabel}
                    <span className="block font-normal text-emerald-400/80 text-[11px] sm:text-xs">
                      Swaps your Performance tint · 5-year warranty
                      {upgradeHasWash && " · free car wash"}
                    </span>
                  </p>
                </div>
                <Button
                  onClick={handleUpgradeToCeramic}
                  size="sm"
                  className="h-8 px-3 bg-emerald-500 hover:bg-emerald-600 text-white font-rajdhani font-bold text-xs sm:text-sm whitespace-nowrap flex-shrink-0"
                  data-testid="ceramic-upgrade-btn"
                >
                  Upgrade +€{calculateCeramicUpgradePrice()}
                </Button>
              </>
            )}

            {bannerMode === "wash-included" && (
              <div className="flex items-center gap-2 min-w-0"
                   data-testid="wash-perk-selected-notice">
                <Droplets className="w-4 h-4 text-cyan-300 flex-shrink-0" />
                <p className="text-cyan-200 font-rajdhani font-bold text-xs sm:text-sm">
                  Free car wash included with your selection
                </p>
              </div>
            )}

            {bannerMode === "wash-nudge" && (
              <>
                <div className="flex items-center gap-2 min-w-0"
                     data-testid="wash-perk-upgrade-banner">
                  <Droplets className="w-4 h-4 text-cyan-300 flex-shrink-0" />
                  <p className="text-cyan-200 font-rajdhani text-xs sm:text-sm leading-tight min-w-0">
                    Add <span className="font-bold">{washNudgeTarget.name}</span> and
                    your car wash is on us
                    {wash.endsOn(washNudgeTarget.service_id) && (
                      <span className="block text-cyan-400/70 text-[11px] sm:text-xs">
                        until {wash.endsOn(washNudgeTarget.service_id)}
                      </span>
                    )}
                  </p>
                </div>
                <Button
                  variant="outline" size="sm"
                  onClick={() => handleServiceClick(washNudgeTarget.service_id, washNudgeTarget)}
                  className="h-8 px-3 border-cyan-400 text-cyan-300 hover:bg-cyan-500/10 text-xs whitespace-nowrap flex-shrink-0"
                  data-testid="wash-perk-upgrade-add-btn"
                >
                  Add €{getServicePrice(washNudgeTarget)}
                </Button>
              </>
            )}

            {bannerMode === "addon" && (
              <>
                <div className="flex items-center gap-2 min-w-0"
                     data-testid="addon-upsell-banner">
                  <Sparkles className="w-4 h-4 text-saturn-orange flex-shrink-0" />
                  <p className="text-saturn-orange font-rajdhani font-bold text-xs sm:text-sm leading-tight min-w-0">
                    {addOnUpsell.message}
                    <span className="block font-normal text-slate-400 text-[11px] sm:text-xs">
                      Adds {addOnUpsell.upsell_service.name}
                    </span>
                  </p>
                </div>
                <Button
                  variant="outline" size="sm"
                  onClick={() => toggleService(addOnUpsell.upsell_service.service_id,
                                               addOnUpsell.upsell_service)}
                  className="h-8 px-3 border-saturn-orange text-saturn-orange hover:bg-saturn-orange/10 text-xs whitespace-nowrap flex-shrink-0"
                  data-testid="addon-upsell-add-btn"
                >
                  Add +€{getServicePrice(addOnUpsell.upsell_service)}
                </Button>
              </>
            )}
          </div>
        </motion.div>
      )}
      </div>

      <div className="text-center mb-8">
        <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white">
          Choose Your Services
        </h2>
        <p className="text-slate-400 mt-2 font-inter">
          Select services for your {data.year} {data.make} {resolveVehicleModel({ model: data.model, model_other: data.model_other })}
        </p>
      </div>

      {/* Services by Super-Category (Tabs) */}
      <Tabs value={activeServiceTab} onValueChange={setActiveServiceTab} className="w-full">
        <TabsList
          className="sticky top-32 z-10 -mx-4 px-4 h-auto w-full flex flex-wrap items-center justify-center gap-1 sm:gap-2 bg-saturn-black/95 backdrop-blur-lg border-b border-slate-800/50 rounded-none py-2"
          data-testid="service-supertabs"
        >
          {superTabsWithContent.map((tab) => {
            const Icon = tab.icon;
            const count = selectedCountByTab[tab.id] || 0;
            return (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                data-testid={`service-tab-${tab.id}`}
                className="rounded-full px-3 sm:px-4 py-2 text-xs sm:text-sm font-rajdhani font-medium border border-slate-700/70 bg-slate-900/40 text-slate-300 gap-1.5 sm:gap-2 hover:border-saturn-orange/50 hover:text-white data-[state=active]:bg-saturn-orange data-[state=active]:text-white data-[state=active]:border-saturn-orange data-[state=active]:shadow-none transition-colors"
              >
                <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>{tab.label}</span>
                {count > 0 && (
                  <span
                    className="ml-1 min-w-[1.25rem] px-1.5 py-0.5 rounded-full bg-white/20 text-[0.65rem] leading-none flex items-center justify-center font-bold"
                    data-testid={`service-tab-${tab.id}-count`}
                  >
                    {count}
                  </span>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {superTabsWithContent.map((tab) => (
          <TabsContent
            key={tab.id}
            value={tab.id}
            className="mt-6 focus-visible:ring-0"
            data-testid={`service-tab-panel-${tab.id}`}
          >
            <div className="space-y-8">
              {tab.categories.map((category) => {
                const categoryServices = groupedServices[category];
                if (!categoryServices || categoryServices.length === 0) return null;

                return (
                  <div key={category} id={`category-${category}`} className="scroll-mt-4">
                    <h3 className="font-rajdhani font-bold text-lg text-saturn-orange uppercase tracking-wider mb-4">
                      {categoryLabels[category] || category}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {categoryServices.map((service) => (
                  <div
                    key={service.service_id}
                    onClick={() => handleServiceClick(service.service_id, service)}
                    className={`glass rounded-xl p-4 cursor-pointer transition-all ${
                      service.out_of_stock 
                        ? 'opacity-50 cursor-not-allowed border-red-500/30'
                        : (partialTintServices.includes(service.service_id) && hasFullCarTint)
                        ? 'opacity-60 cursor-not-allowed border-blue-500/30'
                        : (fullCarTintServices.includes(service.service_id) && hasPartialTint && !data.services.includes(service.service_id))
                        ? 'opacity-60 cursor-not-allowed border-yellow-500/30'
                        : service.requires_quote
                        ? 'border-blue-500/30 hover:border-blue-500/50'
                        : service.has_color_option
                        ? 'border-purple-500/30 hover:border-purple-500/50'
                        : data.services.includes(service.service_id)
                        ? "border-saturn-orange bg-saturn-orange/10"
                        : "hover:border-saturn-orange/50"
                    }`}
                    data-testid={`service-${service.service_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-rajdhani font-bold text-white">{service.name}</h4>
                          {service.out_of_stock && (
                            <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded font-rajdhani">
                              OUT OF STOCK
                            </span>
                          )}
                          {service.requires_quote && (
                            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded font-rajdhani">
                              QUOTE REQUIRED
                            </span>
                          )}
                          {service.has_color_option && !service.out_of_stock && (
                            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded font-rajdhani">
                              SELECT COLOR
                            </span>
                          )}
                          {/* Ceramic Coating requires buffing indicator */}
                          {service.service_id === 'svc_paint_ceramic_coating' && (
                            <span className={`px-2 py-0.5 text-xs rounded font-rajdhani ${
                              ['svc_buff_stage1', 'svc_buff_stage2', 'svc_buff_stage3'].some(id => data.services.includes(id))
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {['svc_buff_stage1', 'svc_buff_stage2', 'svc_buff_stage3'].some(id => data.services.includes(id))
                                ? '✓ BUFFING SELECTED'
                                : 'REQUIRES BUFFING'
                              }
                            </span>
                          )}
                          {/* Show "INCLUDED IN FULL CAR" badge when partial tint is blocked */}
                          {partialTintServices.includes(service.service_id) && hasFullCarTint && (
                            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded font-rajdhani">
                              INCLUDED IN FULL CAR
                            </span>
                          )}
                          {/* Show "DESELECT PARTIAL TINTS" badge when trying to add full car */}
                          {fullCarTintServices.includes(service.service_id) && hasPartialTint && !data.services.includes(service.service_id) && (
                            <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded font-rajdhani">
                              DESELECT REAR/FRONT FIRST
                            </span>
                          )}
                          {wash.isCovered(service.service_id) && !service.out_of_stock && (
                            <WashPerkBadge
                              endDate={wash.endsOn(service.service_id)}
                              testId={`wash-perk-badge-${service.service_id}`}
                            />
                          )}
                          {service.warranty && !service.out_of_stock && !service.requires_quote && !service.has_color_option && service.service_id !== 'svc_paint_ceramic_coating' && (
                            <span className="px-2 py-0.5 bg-saturn-orange/20 text-saturn-orange text-xs rounded font-rajdhani">
                              {service.warranty}
                            </span>
                          )}
                        </div>
                        {/* The catalog hardcodes a wash into two ceramic
                            descriptions and perk chips ("(includes FREE car
                            wash)", a "FREE Carwash" chip). Both are stripped
                            ALWAYS, so the rule-driven FREE CAR WASH badge
                            above is the single place a wash is ever
                            mentioned: three wash mentions on one card was
                            clutter, and the hardcoded two kept promising a
                            wash after the rule was paused. */}
                        <p className="text-slate-400 text-sm mt-1">
                          {stripWashPhrases(service.description)}
                        </p>
                        {(() => {
                          const perks = (service.perks || []).filter(
                            (perk) => !/car\s*wash/i.test(perk));
                          if (!perks.length) return null;
                          return (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {perks.map((perk, i) => (
                                <span key={i} className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                                  {perk}
                                </span>
                              ))}
                            </div>
                          );
                        })()}
                        <div className="flex items-center gap-4 mt-2">
                          {service.requires_quote ? (
                            <span className="text-blue-400 font-rajdhani font-bold flex items-center gap-1">
                              <MessageSquare className="w-4 h-4" />
                              Request Quote
                            </span>
                          ) : (
                            <span className="text-saturn-orange font-orbitron font-bold">
                              €{service.allows_quantity 
                                ? getServicePrice(service) * (data.serviceQuantities[service.service_id] || 1)
                                : getServicePrice(service)
                              }
                              {service.allows_quantity && data.services.includes(service.service_id) && (
                                <span className="text-slate-400 text-xs font-normal ml-1">
                                  (€{getServicePrice(service)} × {data.serviceQuantities[service.service_id] || 1})
                                </span>
                              )}
                            </span>
                          )}
                          {!service.requires_quote && (
                            <span className="text-slate-500 text-sm flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {service.days_required > 0 
                                ? `${service.days_required} day${service.days_required > 1 ? 's' : ''}`
                                : `${Math.round((service.duration_minutes * (service.allows_quantity ? (data.serviceQuantities[service.service_id] || 1) : 1)) / 60 * 10) / 10}h`
                              }
                            </span>
                          )}
                        </div>
                        {/* Quantity selector for tint removal services */}
                        {service.allows_quantity && data.services.includes(service.service_id) && (
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10" onClick={(e) => e.stopPropagation()}>
                            <span className="text-slate-400 text-sm">Quantity:</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const currentQty = data.serviceQuantities[service.service_id] || 1;
                                if (currentQty > 1) {
                                  setData({
                                    ...data,
                                    serviceQuantities: {
                                      ...data.serviceQuantities,
                                      [service.service_id]: currentQty - 1
                                    }
                                  });
                                }
                              }}
                              className="w-8 h-8 rounded-full bg-slate-700 hover:bg-slate-600 text-white flex items-center justify-center"
                            >
                              -
                            </button>
                            <span className="w-8 text-center text-white font-bold">
                              {data.serviceQuantities[service.service_id] || 1}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const currentQty = data.serviceQuantities[service.service_id] || 1;
                                const maxQty = service.max_quantity || 10;
                                if (currentQty < maxQty) {
                                  setData({
                                    ...data,
                                    serviceQuantities: {
                                      ...data.serviceQuantities,
                                      [service.service_id]: currentQty + 1
                                    }
                                  });
                                }
                              }}
                              className="w-8 h-8 rounded-full bg-slate-700 hover:bg-slate-600 text-white flex items-center justify-center"
                            >
                              +
                            </button>
                          </div>
                        )}
                      </div>
                      {service.requires_quote ? (
                        <ChevronRight className="w-5 h-5 text-blue-400" />
                      ) : (
                        <Checkbox
                          checked={data.services.includes(service.service_id)}
                          disabled={
                            service.out_of_stock || 
                            (partialTintServices.includes(service.service_id) && hasFullCarTint) ||
                            (fullCarTintServices.includes(service.service_id) && hasPartialTint && !data.services.includes(service.service_id))
                          }
                          className="border-saturn-orange data-[state=checked]:bg-saturn-orange"
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Summary */}
      {data.services.length > 0 && (
        <div className="glass rounded-xl p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <div>
                <span className="text-slate-400 font-rajdhani">Duration:</span>
                <span className="text-white font-bold ml-2">
                  {totalDays > 0 
                    ? `${totalDays} day${totalDays > 1 ? 's' : ''}`
                    : `${Math.round(totalDuration / 60 * 10) / 10} hours`
                  }
                </span>
              </div>
              {totalDays > 0 && (
                <div className="text-sm text-yellow-400 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Vehicle will be kept overnight
                </div>
              )}
            </div>
            <div>
              <span className="text-slate-400 font-rajdhani">Total Price:</span>
              <span className="text-saturn-orange font-orbitron font-bold text-xl ml-2">
                €{totalPrice}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Duration Disclaimer */}
      <div className="mt-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-400 font-rajdhani font-bold text-sm">Approximate Duration Times</p>
            <p className="text-slate-400 text-sm mt-1">
              All job durations shown are estimates for guidance only. Actual times may vary depending on vehicle condition, 
              complexity, and other factors. These times should not be taken literally.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Navigation - Back button only (Continue is in sticky header) */}
      <div className="flex justify-between pt-6">
        <Button
          variant="outline"
          onClick={onBack}
          className="border-slate-700 text-slate-300 hover:bg-slate-800"
        >
          <ChevronLeft className="mr-2 w-5 h-5" /> Back
        </Button>
        
        {/* Also show continue at bottom for convenience */}
        <Button
          onClick={onNext}
          disabled={!canProceed}
          className="bg-saturn-orange hover:bg-saturn-orange-hover text-white font-rajdhani font-bold px-8 py-3"
          data-testid="service-next-btn"
        >
          Continue <ChevronRight className="ml-2 w-5 h-5" />
        </Button>
      </div>
      
      {/* Color Selection Modal for Roof Wrap / Chrome Delete */}
      <ColorSelectionModal
        isOpen={colorModalOpen}
        onClose={() => {
          setColorModalOpen(false);
          setColorModalService(null);
        }}
        service={colorModalService}
        onSelectGlossBlack={handleGlossBlackSelect}
        onSelectCustom={handleCustomColorSelect}
      />
      
      <QuoteRequestModal
        isOpen={quoteModalOpen}
        onClose={() => {
          setQuoteModalOpen(false);
          setQuoteModalService(null);
        }}
        service={quoteModalService}
        vehicleData={{
          make: data.make,
          model: data.model,
          model_other: data.model_other,
          year: data.year,
          size: data.size
        }}
      />
      
      {/* Tesla Model 3 Rear Glass Selection Modal */}
      <TeslaRearGlassModal
        isOpen={teslaRearGlassModalOpen}
        onClose={() => {
          setTeslaRearGlassModalOpen(false);
          setTeslaRearGlassService(null);
        }}
        service={teslaRearGlassService}
        onSelectHalf={handleTeslaHalfRear}
        onSelectFull={handleTeslaFullRear}
        basePrice={teslaRearGlassService ? 
          (teslaRearGlassService.price_by_size?.[getPricingSize()] || teslaRearGlassService.base_price || 0) : 0}
      />
    </motion.div>
  );
};

// Step 3: Date & Time Selection
const DateTimeStep = ({ data, setData, onNext, onBack }) => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [closedReason, setClosedReason] = useState(null);
  const [dayInfo, setDayInfo] = useState(null);

  const fetchSlots = useCallback(async (date) => {
    setLoading(true);
    setClosedReason(null);
    setSlots([]);
    setDayInfo(null);
    try {
      // Use local date to avoid timezone shifting issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      const response = await axios.get(`${API}/bookings/slots/loc_saturn_duleek/${dateStr}`);
      
      if (!response.data.available) {
        setClosedReason(response.data.reason || "Location closed");
        setSlots([]);
      } else {
        setClosedReason(null);
        setSlots(response.data.slots || []);
      }
      
      const premiumCharge = response.data.premium_charge || 0;
      setDayInfo({
        special_name: response.data.special_name,
        premium_charge: premiumCharge,
        open_time: response.data.open_time,
        close_time: response.data.close_time,
        lunch_break: response.data.lunch_break
      });
      // Store premium charge in data for checkout
      setData(prev => ({ ...prev, premiumCharge }));
    } catch (error) {
      toast.error("Failed to load time slots");
      setSlots([]);
      setClosedReason("Failed to load availability");
    } finally {
      setLoading(false);
    }
  }, [setData]);

  const handleDateSelect = (date) => {
    // Guard against undefined date (clicking on disabled dates)
    if (!date) return;
    
    setSelectedDate(date);
    // Use local date formatting to avoid timezone shift (toISOString converts to UTC)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    setData({ ...data, date: dateStr, time: "", premiumCharge: 0 });
    fetchSlots(date);
  };

  // Legacy Wed/Fri 2-week hardcoded block removed 2026-02 — admins now
  // manage schedule holds through the Block Calendar (db.schedule_blocks),
  // and the /api/bookings/slots endpoint applies them per-day.
  // Only the "no past dates" restriction stays here on the client.
  const disabledDays = [{ before: new Date() }];

  const canProceed = data.date && data.time;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white">
          Choose Date & Time
        </h2>
        <p className="text-slate-400 mt-2 font-inter">
          Select your preferred appointment slot
        </p>
      </div>

      {/* Special Day Info Banner */}
      {dayInfo?.special_name && (
        <div className={`p-4 rounded-xl mb-6 ${
          dayInfo.premium_charge > 0 
            ? 'bg-purple-500/10 border border-purple-500/30' 
            : 'bg-blue-500/10 border border-blue-500/30'
        }`}>
          <div className="flex items-center gap-3">
            <Star className={`w-5 h-5 ${dayInfo.premium_charge > 0 ? 'text-purple-400' : 'text-blue-400'}`} />
            <div>
              <p className={`font-rajdhani font-bold ${dayInfo.premium_charge > 0 ? 'text-purple-400' : 'text-blue-400'}`}>
                {dayInfo.special_name}
              </p>
              <p className="text-slate-400 text-sm">
                Hours: {dayInfo.open_time} - {dayInfo.close_time}
                {dayInfo.lunch_break && ` | Lunch: ${dayInfo.lunch_break}`}
                {dayInfo.premium_charge > 0 && (
                  <span className="text-purple-400 font-bold ml-2">
                    +€{dayInfo.premium_charge} premium charge applies
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Calendar */}
        <div className="glass rounded-xl p-4">
          <CalendarComponent
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            disabled={disabledDays}
            className="w-full"
            classNames={{
              months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
              month: "space-y-4 w-full",
              caption: "flex justify-center pt-1 relative items-center text-white font-rajdhani",
              caption_label: "text-lg font-bold",
              nav: "space-x-1 flex items-center",
              nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-white",
              table: "w-full border-collapse space-y-1",
              head_row: "flex",
              head_cell: "text-slate-400 rounded-md w-9 font-normal text-[0.8rem] flex-1 text-center",
              row: "flex w-full mt-2",
              cell: "text-center text-sm p-0 relative flex-1 [&:has([aria-selected])]:bg-saturn-orange/20 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
              day: "h-9 w-full p-0 font-semibold aria-selected:opacity-100 hover:bg-saturn-orange/20 rounded-md text-white",
              day_selected: "bg-saturn-orange text-white hover:bg-saturn-orange-hover font-bold",
              day_today: "border-2 border-saturn-orange text-saturn-orange font-bold",
              day_disabled: "!text-slate-500 !opacity-50 cursor-not-allowed line-through",
              day_outside: "!text-slate-600 !opacity-40",
            }}
          />
          <p className="text-xs text-slate-500 mt-2 text-center">
            Mon-Sat: 10am-6pm | Thu: 12pm-8pm | Sun: 10am-2pm (+€50)
          </p>
        </div>

        {/* Time Slots */}
        <div className="space-y-4">
          <h3 className="font-rajdhani font-bold text-lg text-white">
            {selectedDate
              ? `Available Slots for ${selectedDate.toLocaleDateString("en-IE", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })}`
              : "Select a date to view available times"}
          </h3>

          <SlotGrid
            slots={slots}
            selectedTime={data.time}
            loading={loading}
            closedReason={closedReason}
            emptyMessage={selectedDate ? "No slots available for this date" : ""}
            onSlotClick={(slot) => setData({ ...data, time: slot.time })}
            mode="book"
          />
        </div>
      </div>

      <div className="flex justify-between pt-6">
        <Button
          variant="outline"
          onClick={onBack}
          className="border-slate-700 text-slate-300 hover:bg-slate-800"
        >
          <ChevronLeft className="mr-2 w-5 h-5" /> Back
        </Button>
        <Button
          onClick={onNext}
          disabled={!canProceed}
          className="bg-saturn-orange hover:bg-saturn-orange-hover text-white font-rajdhani font-bold px-8 py-3"
          data-testid="datetime-next-btn"
        >
          Continue <ChevronRight className="ml-2 w-5 h-5" />
        </Button>
      </div>
    </motion.div>
  );
};

// Step 4: Customer Details & Checkout
const CheckoutStep = ({ data, setData, onBack, services, slotHold }) => {
  const [loading, setLoading] = useState(false);
  const wash = useWashPerk();
  const [overnightInfo, setOvernightInfo] = useState(null);
  const [errors, setErrors] = useState({});
  // Promo code validation state — local because it's transient UI:
  //   idle  → nothing entered yet
  //   loading → apply-click in flight
  //   valid → server returned ok:true; discount shown + exclusivity warning
  //   invalid → server returned ok:false; friendly reason shown
  const [promoStatus, setPromoStatus] = useState("idle");
  const [promoResult, setPromoResult] = useState(null); // set on valid
  const [promoError, setPromoError] = useState(null);   // set on invalid
  const navigate = useNavigate();

  // Format timer display (MM:SS)
  const formatTimer = (seconds) => {
    if (!seconds || seconds <= 0) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Irish phone: +353, 083, 085, 086, 087, 089 or landline formats
    const phoneRegex = /^(\+353|0)[1-9][0-9]{7,9}$/;
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    return cleanPhone.length >= 10 && phoneRegex.test(cleanPhone);
  };

  const validateName = (name) => {
    // At least 2 characters, letters and spaces only
    const nameRegex = /^[a-zA-Z\s'-]{2,}$/;
    return nameRegex.test(name.trim());
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setData({ ...data, customerName: value });
    if (value && !validateName(value)) {
      setErrors(prev => ({ ...prev, name: 'Please enter a valid name (letters only, min 2 characters)' }));
    } else {
      setErrors(prev => ({ ...prev, name: null }));
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setData({ ...data, customerEmail: value });
    if (value && !validateEmail(value)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
    } else {
      setErrors(prev => ({ ...prev, email: null }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow digits, spaces, +, -, ()
    const sanitized = value.replace(/[^0-9+\s\-\(\)]/g, '');
    setData({ ...data, customerPhone: sanitized });
    if (sanitized && !validatePhone(sanitized)) {
      setErrors(prev => ({ ...prev, phone: 'Please enter a valid Irish phone number (e.g., +353 85 123 4567)' }));
    } else {
      setErrors(prev => ({ ...prev, phone: null }));
    }
  };

  // Reuse the same pricing tier logic with override for luxury saloons
  const getCheckoutPricingSize = () => {
    if (data.model && PRICING_TIER_OVERRIDE[data.model]) {
      return PRICING_TIER_OVERRIDE[data.model];
    }
    const sizeMap = { 'small': 'hatch', 'medium': 'saloon', 'large': 'estate', 'suv': 'suv', 'hatch': 'hatch', 'saloon': 'saloon', 'estate': 'estate' };
    return sizeMap[data.size] || 'saloon';
  };

  const selectedServices = services.filter((s) => data.services.includes(s.service_id));
  
  // Tesla Model 3 full rear glass premium
  const TESLA_FULL_REAR_PREMIUM = 40;
  
  // Calculate total Tesla rear glass premium
  const teslaRearGlassPremium = Object.entries(data.teslaRearGlassOption || {}).reduce(
    (sum, [serviceId, option]) => sum + (option === 'full' ? TESLA_FULL_REAR_PREMIUM : 0),
    0
  );
  
  const servicesTotal = selectedServices.reduce(
    (sum, s) => {
      const basePrice = s.price_by_size[getCheckoutPricingSize()] || s.price_by_size[data.size] || s.base_price;
      const quantity = s.allows_quantity ? (data.serviceQuantities[s.service_id] || 1) : 1;
      return sum + (basePrice * quantity);
    },
    0
  );
  const sundayPremium = data.premiumCharge || 0;
  const totalPrice = servicesTotal + sundayPremium + teslaRearGlassPremium;
  const totalDuration = selectedServices.reduce((sum, s) => {
    const quantity = s.allows_quantity ? (data.serviceQuantities[s.service_id] || 1) : 1;
    return sum + (s.duration_minutes * quantity);
  }, 0);
  const totalDays = selectedServices.reduce((sum, s) => sum + (s.days_required || 0), 0);

  // Check if total is below deposit minimum (€50) - show phone prompt
  const isBelowMinimum = totalPrice > 0 && totalPrice < 50;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setOvernightInfo(null);

    try {
      // Create booking - include accept_overnight if user has acknowledged
      const bookingResponse = await axios.post(`${API}/bookings/`, {
        location_id: "loc_saturn_duleek",
        service_ids: data.services,
        service_quantities: data.serviceQuantities,
        tesla_rear_glass_option: data.teslaRearGlassOption,
        vehicle_make: data.make,
        vehicle_model: data.model,
        vehicle_model_other: data.model === "Other" ? (data.model_other || null) : null,
        vehicle_year: data.year,
        vehicle_size: getCheckoutPricingSize(),
        vehicle_reg: data.vehicleReg || null,
        customer_name: data.customerName,
        customer_email: data.customerEmail,
        customer_phone: data.customerPhone,
        booking_date: data.date,
        booking_time: data.time,
        notes: data.notes,
        upsell_accepted: false,
        accept_overnight: data.acceptOvernight || false,
        // Only send the code when the customer has successfully
        // applied it (server-side re-validated). Blocks a stale
        // typed-but-unapplied string from silently reaching backend.
        promo_code: promoStatus === "valid" && data.promoCode ? data.promoCode : null,
      });

      const bookingId = bookingResponse.data.booking_id;
      const collectionDate = bookingResponse.data.collection_date;
      const collectionTime = bookingResponse.data.collection_time;

      // GA4 funnel — form successfully accepted server-side. Value is
      // total_price minus deposit (rough intent signal; the trusted
      // conversion value fires from BookingSuccessPage on `purchase`).
      trackBookingFormSubmitted({
        value: Number(bookingResponse.data.total_price) || 0,
        currency: "EUR",
      });

      // GA4 — promotion outcome, if the server ran a promo entry for
      // this booking. Backend returns `promotion` when a promo was
      // triggered; null otherwise. See routes/bookings.py :: create_booking.
      const promo = bookingResponse.data.promotion;
      if (promo && promo.outcome_label) {
        trackPromoEntry({
          outcome_label: promo.outcome_label,
          discount_value: promo.discount_value,
        });
      }

      // Create deposit payment
      const paymentResponse = await axios.post(`${API}/payments/create-deposit`, {
        booking_id: bookingId,
        customer_email: data.customerEmail,
        customer_phone: data.customerPhone,
      });

      // Redirect to Revolut Business Merchant checkout
      if (paymentResponse.data.checkout_url) {
        // Store booking info in localStorage so we can show confirmation after payment
        localStorage.setItem('pendingBooking', JSON.stringify({
          bookingId,
          date: data.date,
          time: data.time,
          collectionDate,
          collectionTime
        }));
        
        // Redirect to real Revolut checkout
        window.location.href = paymentResponse.data.checkout_url;
        return;
      }

      // Fallback if no checkout URL (shouldn't happen)
      toast.error("Payment setup failed. Please try again.");
    } catch (error) {
      console.error("Booking error:", error);
      
      // Check if this is an overnight requirement error
      if (error.response?.status === 422 && error.response?.data?.detail?.requires_overnight) {
        const detail = error.response.data.detail;
        setOvernightInfo({
          message: detail.message,
          collectionDate: detail.collection_date,
          daysRequired: detail.days_required,
          reason: detail.reason
        });
      } else {
        const errorMsg = typeof error.response?.data?.detail === 'string' 
          ? error.response.data.detail 
          : "Failed to create booking";
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptOvernight = () => {
    setData({ ...data, acceptOvernight: true });
    setOvernightInfo(null);
    // Auto-submit after accepting
    setTimeout(() => {
      document.getElementById('booking-form').requestSubmit();
    }, 100);
  };

  const canSubmit =
    data.customerName && validateName(data.customerName) &&
    data.customerEmail && validateEmail(data.customerEmail) &&
    data.customerPhone && validatePhone(data.customerPhone) &&
    data.agreeTerms &&
    !errors.name && !errors.email && !errors.phone;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white">
          Complete Your Booking
        </h2>
        <p className="text-slate-400 mt-2 font-inter">
          Enter your details and secure your appointment with a €50 deposit
        </p>
        
        {/* Slot Hold Timer */}
        {slotHold && slotHold.timeRemaining > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full ${
              slotHold.timeRemaining <= 60 
                ? 'bg-red-500/20 border border-red-500/50' 
                : slotHold.timeRemaining <= 120 
                  ? 'bg-yellow-500/20 border border-yellow-500/50'
                  : 'bg-saturn-orange/20 border border-saturn-orange/50'
            }`}
          >
            <Clock className={`w-4 h-4 ${
              slotHold.timeRemaining <= 60 ? 'text-red-400 animate-pulse' : 'text-saturn-orange'
            }`} />
            <span className={`font-rajdhani font-bold ${
              slotHold.timeRemaining <= 60 ? 'text-red-400' : 'text-saturn-orange'
            }`}>
              Slot reserved: {formatTimer(slotHold.timeRemaining)}
            </span>
          </motion.div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Overnight Notification Modal */}
        <AnimatePresence>
          {overnightInfo && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="glass rounded-2xl p-6 max-w-md w-full"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-yellow-400" />
                  </div>
                  <h3 className="font-orbitron text-xl font-bold text-white">Overnight Required</h3>
                </div>
                <p className="text-slate-300 mb-4">{overnightInfo.message}</p>
                <div className="glass rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Collection Date:</span>
                    <span className="text-saturn-orange font-rajdhani font-bold">
                      {new Date(overnightInfo.collectionDate).toLocaleDateString("en-IE", {
                        weekday: "long",
                        month: "long",
                        day: "numeric"
                      })}
                    </span>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setOvernightInfo(null)}
                    className="flex-1 border-slate-700 text-slate-300"
                  >
                    Go Back
                  </Button>
                  <Button
                    onClick={handleAcceptOvernight}
                    className="flex-1 bg-saturn-orange hover:bg-saturn-orange-hover"
                  >
                    Accept & Continue
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Customer Form */}
        <form id="booking-form" onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300 font-rajdhani">Full Name *</Label>
              <Input
                value={data.customerName}
                onChange={handleNameChange}
                placeholder="John Smith"
                className={`bg-slate-900/50 border-slate-700 text-white ${errors.name ? 'border-red-500' : ''}`}
                required
                data-testid="customer-name-input"
              />
              {errors.name && (
                <p className="text-red-400 text-xs mt-1">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 font-rajdhani">Email *</Label>
              <Input
                type="email"
                value={data.customerEmail}
                onChange={handleEmailChange}
                placeholder="john@example.com"
                className={`bg-slate-900/50 border-slate-700 text-white ${errors.email ? 'border-red-500' : ''}`}
                required
                data-testid="customer-email-input"
              />
              {errors.email && (
                <p className="text-red-400 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 font-rajdhani">Phone *</Label>
              <Input
                type="tel"
                value={data.customerPhone}
                onChange={handlePhoneChange}
                placeholder="+353 85 123 4567"
                className={`bg-slate-900/50 border-slate-700 text-white ${errors.phone ? 'border-red-500' : ''}`}
                required
                data-testid="customer-phone-input"
              />
              {errors.phone && (
                <p className="text-red-400 text-xs mt-1">{errors.phone}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 font-rajdhani">Vehicle Registration (Optional)</Label>
              <Input
                value={data.vehicleReg}
                onChange={(e) => setData({ ...data, vehicleReg: e.target.value.toUpperCase() })}
                placeholder="e.g., 231-D-12345"
                className="bg-slate-900/50 border-slate-700 text-white uppercase"
                data-testid="vehicle-reg-input"
              />
              <p className="text-slate-500 text-xs">For warranty tracking purposes</p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 font-rajdhani">Notes (Optional)</Label>
              <Input
                value={data.notes}
                onChange={(e) => setData({ ...data, notes: e.target.value })}
                placeholder="Any special requests or notes"
                className="bg-slate-900/50 border-slate-700 text-white"
                data-testid="booking-notes-input"
              />
              <p className="text-slate-500 text-xs">
                Please avoid including other personal or sensitive details here.
              </p>
            </div>
          </div>

          {/* Promo code — optional, word-of-mouth only (no banner shown
              elsewhere). Applying a code opts the booking OUT of any
              active wheel/jackpot campaign. Removing the code
              re-enables auto-entry. */}
          <div className="space-y-2 p-4 rounded-lg bg-slate-900/40 border border-slate-800">
            <Label
              htmlFor="promo-code-input"
              className="text-slate-300 font-rajdhani"
            >
              Have a promo code?
            </Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                id="promo-code-input"
                value={data.promoCode}
                onChange={(e) => {
                  setData({ ...data, promoCode: e.target.value.toUpperCase() });
                  // Any edit clears the previous validation state so a
                  // stale "valid" pill can't cling to a mutated code.
                  if (promoStatus !== "idle") {
                    setPromoStatus("idle");
                    setPromoResult(null);
                    setPromoError(null);
                  }
                }}
                placeholder="e.g., SATURN20"
                disabled={promoStatus === "loading" || promoStatus === "valid"}
                className="bg-slate-900/50 border-slate-700 text-white uppercase flex-1"
                data-testid="promo-code-input"
              />
              {promoStatus !== "valid" ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={
                    promoStatus === "loading" ||
                    !data.promoCode ||
                    !data.promoCode.trim()
                  }
                  onClick={async () => {
                    setPromoStatus("loading");
                    setPromoError(null);
                    try {
                      const firstServiceId =
                        (data.services && data.services[0]) || null;
                      const deposit = Math.min(50, Number(totalPrice) || 0);
                      const resp = await axios.post(
                        `${API}/promotions/redeem-code`,
                        {
                          code: data.promoCode.trim(),
                          service_id: firstServiceId,
                          total_price: Number(totalPrice) || 0,
                          deposit_paid: deposit,
                        }
                      );
                      if (resp.data && resp.data.ok) {
                        setPromoResult(resp.data);
                        setPromoStatus("valid");
                      } else {
                        setPromoError(
                          (resp.data && resp.data.reason) ||
                            "That code isn't recognised."
                        );
                        setPromoStatus("invalid");
                      }
                    } catch (err) {
                      setPromoError(
                        (err.response &&
                          err.response.data &&
                          err.response.data.detail) ||
                          "We couldn't check that code — please try again."
                      );
                      setPromoStatus("invalid");
                    }
                  }}
                  className="border-saturn-orange text-saturn-orange hover:bg-saturn-orange hover:text-black"
                  data-testid="promo-code-apply-btn"
                >
                  {promoStatus === "loading" ? "Checking…" : "Apply"}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setData({ ...data, promoCode: "" });
                    setPromoStatus("idle");
                    setPromoResult(null);
                    setPromoError(null);
                  }}
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                  data-testid="promo-code-remove-btn"
                >
                  Remove code
                </Button>
              )}
            </div>
            {promoStatus === "valid" && promoResult && (
              <div
                className="mt-2 space-y-2 text-sm"
                data-testid="promo-code-applied-banner"
              >
                <p className="text-emerald-400 font-rajdhani font-bold">
                  Code applied: {promoResult.outcome_label}. Remaining
                  balance after deposit: €
                  {Number(promoResult.remaining_balance_due).toFixed(2)}.
                </p>
                <p
                  className="text-amber-300/90 text-xs leading-relaxed"
                  data-testid="promo-code-exclusivity-warning"
                >
                  Using this code means you won&apos;t be entered into any
                  other active promotions on this booking.
                </p>
              </div>
            )}
            {promoStatus === "invalid" && promoError && (
              <p
                className="mt-2 text-sm text-red-400"
                data-testid="promo-code-error"
              >
                {promoError}
              </p>
            )}
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              checked={data.agreeTerms}
              onCheckedChange={(checked) => setData({ ...data, agreeTerms: checked })}
              className="border-saturn-orange data-[state=checked]:bg-saturn-orange mt-1"
              data-testid="agree-terms-checkbox"
            />
            <label className="text-sm text-slate-400">
              I agree to the €50 non-refundable deposit and SaturnStyling's terms of service.
              This deposit will be deducted from your final bill.
            </label>
          </div>

          {/* Below minimum warning - show phone number instead of booking */}
          {isBelowMinimum && (
            <div className="p-4 rounded-lg bg-amber-500/20 border border-amber-500/50">
              <div className="flex items-start gap-3">
                <Phone className="w-6 h-6 text-amber-400 mt-0.5" />
                <div>
                  <p className="text-amber-200 font-rajdhani font-bold">
                    Minimum Booking Value Not Met
                  </p>
                  <p className="text-amber-200/80 text-sm mt-1">
                    Orders under €50 require phone booking. Please call us to arrange:
                  </p>
                  <a 
                    href="tel:+353858237373" 
                    className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-lg transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    +353 85 823 7373
                  </a>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              <ChevronLeft className="mr-2 w-5 h-5" /> Back
            </Button>
            {!isBelowMinimum && (
              <Button
                type="submit"
                disabled={!canSubmit || loading}
                className="bg-saturn-orange hover:bg-saturn-orange-hover text-white font-rajdhani font-bold px-8 py-3 neon-glow"
                data-testid="confirm-booking-btn"
              >
                {loading ? (
                  "Processing..."
                ) : (
                  <>
                    Pay €50 Deposit <CreditCard className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            )}
          </div>
        </form>

        {/* Order Summary */}
        <div className="space-y-4">
          <Card className="glass border-slate-700">
            <CardHeader>
              <CardTitle className="font-rajdhani text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-saturn-orange" />
                Booking Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Vehicle */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50">
                <Car className="w-5 h-5 text-saturn-orange" />
                <div>
                  <div className="text-white font-rajdhani">
                    {data.year} {data.make} {resolveVehicleModel({ model: data.model, model_other: data.model_other })}
                  </div>
                  <div className="text-slate-400 text-sm capitalize">{data.size} vehicle</div>
                </div>
              </div>

              {/* Date/Time */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50">
                <Calendar className="w-5 h-5 text-saturn-orange" />
                <div>
                  <div className="text-white font-rajdhani">
                    {new Date(data.date).toLocaleDateString("en-IE", {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>
                  <div className="text-slate-400 text-sm">{data.time}</div>
                </div>
              </div>

              {/* Services */}
              <div className="border-t border-slate-700 pt-4">
                <h4 className="text-slate-400 text-sm uppercase tracking-wider mb-3">Services</h4>
                {selectedServices.map((service) => {
                  const basePrice = (service.price_by_size[getCheckoutPricingSize()] || service.price_by_size[data.size] || service.base_price) * (service.allows_quantity ? (data.serviceQuantities[service.service_id] || 1) : 1);
                  const teslaOption = data.teslaRearGlassOption?.[service.service_id];
                  const teslaExtra = teslaOption === 'full' ? TESLA_FULL_REAR_PREMIUM : 0;
                  
                  return (
                    <div key={service.service_id} className="py-2">
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <span className="text-slate-300">{service.name}</span>
                          {service.warranty && (
                            <span className="ml-2 text-xs text-saturn-orange">({service.warranty})</span>
                          )}
                        </div>
                        <span className="text-white font-rajdhani">
                          €{basePrice + teslaExtra}
                          {service.allows_quantity && (data.serviceQuantities[service.service_id] || 1) > 1 && (
                            <span className="text-slate-400 text-xs ml-1">
                              (×{data.serviceQuantities[service.service_id] || 1})
                            </span>
                          )}
                        </span>
                      </div>
                      {/* Tesla Model 3 rear glass option indicator */}
                      {teslaOption && (
                        <div className="mt-1 flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            teslaOption === 'full' 
                              ? 'bg-blue-500/20 text-blue-400' 
                              : 'bg-green-500/20 text-green-400'
                          }`}>
                            {teslaOption === 'full' ? 'Full Rear Glass (+€40)' : 'Half Rear Glass'}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Car wash — the perk, never the cost. Same single
                    source of truth as the card badge and the selection
                    notice, so the three can never disagree. */}
                {wash.anyCovered(data.services) && (
                  <div className="flex justify-between items-center py-2"
                       data-testid="wash-perk-summary-line">
                    <span className="text-cyan-300 flex items-center gap-2">
                      <Droplets className="w-4 h-4" /> Car wash
                    </span>
                    <span className="text-cyan-300 font-rajdhani font-bold">FREE</span>
                  </div>
                )}
              </div>

              {/* Totals */}
              <div className="border-t border-slate-700 pt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-400">Total Duration</span>
                  <span className="text-white">{totalDuration} mins</span>
                </div>
                {sundayPremium > 0 && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Services Subtotal</span>
                      <span className="text-white">€{servicesTotal}</span>
                    </div>
                    <div className="flex justify-between text-purple-400">
                      <span className="font-rajdhani">Premium Sunday Charge</span>
                      <span className="font-rajdhani font-bold">+€{sundayPremium}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-400">Total Price</span>
                  <span className="text-white font-orbitron font-bold">€{totalPrice}</span>
                </div>
                <div className="flex justify-between text-saturn-orange">
                  <span className="font-rajdhani font-bold">Deposit Due Now</span>
                  <span className="font-orbitron font-bold">€50</span>
                </div>
                <div className="flex justify-between text-slate-400 text-sm">
                  <span>Balance Due at Appointment</span>
                  <span>€{totalPrice - 50}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="glass rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-saturn-orange flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-400">
              Your €50 deposit is non-refundable and secures your booking slot. 
              The remaining balance (€{totalPrice - 50}) is due at your appointment.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Main Booking Page
const BookingPage = () => {
  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [slotHold, setSlotHold] = useState(null); // {hold_id, expires_at, timeRemaining}
  const [holdExpired, setHoldExpired] = useState(false);
  const [data, setData] = useState({
    // Vehicle
    make: "",
    model: "",
    model_other: "", // Free-text fallback when model === "Other"
    year: "",
    size: "",
    // Services
    services: [],
    serviceQuantities: {}, // For tint removal services that allow quantity
    teslaRearGlassOption: {}, // Tesla Model 3 rear glass options: { serviceId: 'half' | 'full' }
    // Date/Time
    date: "",
    time: "",
    premiumCharge: 0, // Sunday premium
    // Customer
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    vehicleReg: "", // Registration number for warranty
    notes: "",
    agreeTerms: false,
    acceptOvernight: false,
    // Optional promo code the customer types on the last step. Kept in
    // parent state so it survives step navigation, but validation
    // status lives in CheckoutStep (transient UI).
    promoCode: "",
  });

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services/`);
        setServices(response.data);
      } catch (error) {
        console.error("Failed to load services");
      }
    };
    fetchServices();
    // GA4 funnel — landed on /book. Fires once per mount; the natural
    // React unmount/remount cadence on client-side navigation gives us
    // one begin_checkout per genuine funnel entry.
    trackBookingStarted();
  }, []);

  // Google Ads: intentionally no page-view conversion fire here — the
  // conversion pixel is fired ONLY from BookingSuccessPage.jsx after the
  // backend confirms `status: 'paid'` (see utils/googleAds.js). Prior
  // implementation fired on every booking-page view, polluting the ads
  // account with false conversions. The GA4 `purchase` event is fired
  // from the same server-confirmed point for the same reason — both
  // conversion signals share one trusted trigger.

  // Slot hold countdown timer
  useEffect(() => {
    if (!slotHold || step !== 3) return;
    
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const expiresAt = new Date(slotHold.expires_at).getTime();
      const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
      
      if (remaining <= 0) {
        clearInterval(timer);
        setHoldExpired(true);
        setSlotHold(null);
        toast.error("Your slot reservation has expired. Please select a new time.", {
          duration: 5000
        });
        // Go back to datetime selection
        setStep(2);
      } else {
        setSlotHold(prev => ({ ...prev, timeRemaining: remaining }));
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [slotHold, step]);

  // Release hold when leaving checkout without completing
  useEffect(() => {
    return () => {
      if (slotHold?.hold_id) {
        axios.delete(`${API}/bookings/hold-slot/${slotHold.hold_id}`).catch(() => {});
      }
    };
  }, [slotHold]);

  // Function to hold slot when moving to checkout
  const holdSlotForCheckout = async () => {
    try {
      const response = await axios.post(`${API}/bookings/hold-slot`, {
        location_id: "loc_saturn_duleek",
        booking_date: data.date,
        booking_time: data.time
      });
      setSlotHold({
        hold_id: response.data.hold_id,
        expires_at: response.data.expires_at,
        timeRemaining: response.data.hold_duration_seconds
      });
      setHoldExpired(false);
      return true;
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error("This slot was just taken! Please select another time.");
        return false;
      }
      toast.error("Failed to reserve slot. Please try again.");
      return false;
    }
  };

  const steps = [
    { icon: Car, label: "Vehicle" },
    { icon: Sparkles, label: "Services" },
    { icon: Calendar, label: "Schedule" },
    { icon: CreditCard, label: "Checkout" },
  ];

  return (
    <div
      className="min-h-screen bg-saturn-black"
      style={{ paddingTop: "var(--promo-banner-h, 0px)" }}
    >
      <PromotionBanner />
      {/* Header */}
      <header
        style={{ top: "var(--promo-banner-h, 0px)" }}
        className="glass py-4 sticky z-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3" data-testid="booking-logo nav-logo">
              <div className="w-12 h-12 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tint-bay-engine/artifacts/zdb6hh5n_Eima%20logo%20Speed%20rate%20font%2020x20.png"
                  alt="Saturn Styling"
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="font-orbitron font-bold text-xl text-white hidden sm:block">
                SATURNSTYLING
              </span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-slate-300 hover:text-saturn-orange">
                <ArrowLeft className="mr-2 w-4 h-4" /> Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <StepIndicator currentStep={step} steps={steps} />

        <div className="glass rounded-2xl p-6 md:p-8">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <VehicleStep
                key="vehicle"
                data={data}
                setData={setData}
                onNext={() => { setStep(1); window.scrollTo(0, 0); }}
              />
            )}
            {step === 1 && (
              <ServiceStep
                key="service"
                data={data}
                setData={setData}
                onNext={() => { setStep(2); window.scrollTo(0, 0); }}
                onBack={() => { setStep(0); window.scrollTo(0, 0); }}
              />
            )}
            {step === 2 && (
              <DateTimeStep
                key="datetime"
                data={data}
                setData={setData}
                onNext={async () => {
                  const held = await holdSlotForCheckout();
                  if (held) {
                    setStep(3);
                    window.scrollTo(0, 0);
                  }
                }}
                onBack={() => { setStep(1); window.scrollTo(0, 0); }}
              />
            )}
            {step === 3 && (
              <CheckoutStep
                key="checkout"
                data={data}
                setData={setData}
                onBack={() => {
                  // Release hold when going back
                  if (slotHold?.hold_id) {
                    axios.delete(`${API}/bookings/hold-slot/${slotHold.hold_id}`).catch(() => {});
                    setSlotHold(null);
                  }
                  setStep(2);
                  window.scrollTo(0, 0);
                }}
                services={services}
                slotHold={slotHold}
              />
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default BookingPage;
